import classNames from 'classnames';
import React, { memo, useCallback, useState } from 'react';
import styled from 'styled-components';
import { EMPTY_OBJ } from '../../constants';
import { ButtonColors } from '../../mixins';
import { badgeSize, COLOR, fieldBorderRadius, FONT_SIZE, SIZE } from '../../styles';
import { a11yKey } from '../../utils/ComponentUtil';
import { PopoverTooltip, usePopoverTooltip } from '../PopoverTooltip';
import { type ButtonProps } from './Button.types';

const Button: React.FC<ButtonProps> = memo(
  ({
    tooltip = '',
    refFunc,
    onClick,
    onClickProps,
    onMouseOut,
    onMouseOver,
    attributes,
    active = false,
    backIcon,
    badge,
    children,
    className,
    colorMode = 'dark',
    dataCy,
    dataTestId,
    dataIntercomTarget,
    disabled,
    float,
    htmlButtonType,
    icon,
    iconPosition = 'front',
    id,
    isPrimary = true,
    isSecondary = false,
    iconOnly = false,
    onBlur,
    onFocus,
    size,
    style = EMPTY_OBJ,
    text,
    textStyle = EMPTY_OBJ,
    title,
    type,
    typography = 'uppercase',
    tooltipStyle = EMPTY_OBJ,
    noPadding = false,
  }) => {
    const [tooltipVisible, setTooltipVisible] = useState(false);
    const tooltipPopover = usePopoverTooltip({
      visible: !!tooltip && tooltipVisible,
      placement: 'top',
    });

    const setButtonRef = useCallback(
      (node: HTMLButtonElement) => {
        tooltipPopover.setTriggerRef(node);
        if (refFunc) refFunc(node);
      },
      [refFunc, tooltipPopover],
    );

    const hideTooltip = () => setTooltipVisible(false);
    const showTooltip = () => setTooltipVisible(true);

    const onClickFunc = (event: React.MouseEvent<HTMLButtonElement>) => {
      event.currentTarget.blur();
      if (onClick) {
        onClick(event, onClickProps);
      }
    };

    const onMouseOutFunc = () => {
      hideTooltip();
      onMouseOut?.();
    };

    const onMouseOverFunc = () => {
      showTooltip();
      onMouseOver?.();
    };

    const onMouseBlur = (event: React.FocusEvent<HTMLButtonElement>) => {
      hideTooltip();

      onBlur?.(event);
    };

    const isIconFront = iconPosition === 'front';
    const isIconBack = iconPosition === 'back';

    const combinedClass = classNames(
      'styled',
      size || '',
      type || '',
      typography ? `typo-${typography}` : '',
      {
        active,
        primary: isPrimary && !disabled && type !== 'grouped',
        secondary: isSecondary,
        'icon-only': iconOnly,
        'no-padding': noPadding,
      },
      className,
      `mode-${colorMode}`,
    );

    const tabIndex = active && type === 'grouped' ? -1 : 0;

    return (
      <>
        <StyledButton
          {...attributes}
          className={combinedClass}
          data-cy={dataCy}
          data-testid={dataTestId || dataIntercomTarget}
          data-intercom-target={dataIntercomTarget}
          disabled={disabled}
          id={id}
          onBlur={onMouseBlur}
          onClick={onClickFunc}
          onFocus={onFocus}
          onKeyDown={a11yKey(onClick)}
          ref={setButtonRef}
          style={{ ...(float ? { float } : {}), ...style }}
          tabIndex={tabIndex}
          title={title}
          type={htmlButtonType}
        >
          {isIconFront && icon}
          {badge !== null && badge !== undefined && <span className="badge">{badge}</span>}
          {!iconOnly && (
            <span className="text-wrapper" style={textStyle}>
              {text}
            </span>
          )}
          {!iconOnly && children}
          {isIconBack && icon}
          {!!backIcon && backIcon}
          <ButtonOverlay
            onMouseOut={onMouseOutFunc}
            onMouseOver={onMouseOverFunc}
            disabled={!!disabled}
            data-testid="button-overlay"
          ></ButtonOverlay>
        </StyledButton>
        <PopoverTooltip {...tooltipPopover}>
          {tooltipStyle ? (
            <div style={{ textAlign: 'center', ...tooltipStyle }}>{tooltip}</div>
          ) : (
            tooltip
          )}
        </PopoverTooltip>
      </>
    );
  },
);

// Button Variables
const buttonHeight = '36px';
const shortButtonHeight = '30px';
const thinButtonHeight = '24px';
const smallButtonHeight = '20px';
// Styled Components
const ButtonOverlay = styled.div<{ disabled: boolean }>`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  ${(p) => p.disabled && 'cursor: not-allowed; pointer-events: all;'}
`;
const StyledButton = styled.button`
  display: flex;
  justify-content: center;
  align-items: center;
  border-radius: ${fieldBorderRadius};
  border: none;
  box-sizing: border-box;
  cursor: pointer;
  font-family: inherit;
  font-size: ${FONT_SIZE['13']};
  font-weight: 400;
  height: ${buttonHeight};
  line-height: unset;
  min-width: 80px;
  padding: 2px ${SIZE['12']};
  position: relative;
  width: auto;

  &.typo-uppercase {
    text-transform: uppercase;
  }

  &.typo-capitalize {
    text-transform: capitalize;
  }

  &.mode-dark {
    ${ButtonColors({ badge: COLOR['dark-gray-light'] })}
  }

  &.mode-dark-on-dark {
    ${ButtonColors({
      bg: COLOR['dark-gray-light'],
      fg: COLOR['white'],
      hoverBg: COLOR['primary-light'],
      hoverFg: COLOR['turquoise-darker'],
    })}
  }

  &.mode-line-dark {
    border-style: solid;
    border-width: 1px;
    ${ButtonColors({
      bg: COLOR['dark-gray-darkest'],
      fg: COLOR['primary-light'],
      hoverBg: COLOR['primary-light'],
      hoverFg: COLOR['turquoise-darker'],
      border: COLOR['primary-light'],
      hoverBorder: COLOR['primary-light'],
    })}

    &.active {
      ${ButtonColors({
        badge: COLOR['turquoise-dark'],
        bg: COLOR['turquoise'],
        fg: COLOR['white'],
        hoverBg: COLOR['turquoise'],
        hoverFg: COLOR['white'],
        border: COLOR['turquoise'],
        hoverBorder: COLOR['turquoise-light'],
      })}
    }
  }

  &.mode-line-dark-orange {
    border-style: solid;
    border-width: 1px;
    ${ButtonColors({
      bg: COLOR['dark-gray-darkest'],
      fg: COLOR['orange'],
      hoverBg: COLOR['orange'],
      hoverFg: COLOR['turquoise-darker'],
      border: COLOR['orange'],
      hoverBorder: COLOR['orange'],
    })}

    &.active {
      ${ButtonColors({
        badge: COLOR['orange-dark'],
        bg: COLOR['orange'],
        fg: COLOR['white'],
        hoverBg: COLOR['orange'],
        hoverFg: COLOR['white'],
        border: COLOR['orange'],
        hoverBorder: COLOR['orange-light'],
      })}
    }
  }

  &.mode-line-dark-red {
    border-style: solid;
    border-width: 1px;
    ${ButtonColors({
      bg: COLOR['dark-gray-darkest'],
      fg: COLOR['red'],
      hoverBg: COLOR['red'],
      hoverFg: COLOR['turquoise-darker'],
      border: COLOR['red'],
      hoverBorder: COLOR['red'],
    })}

    &.active {
      ${ButtonColors({
        badge: COLOR['red-dark'],
        bg: COLOR['red'],
        fg: COLOR['white'],
        hoverBg: COLOR['red'],
        hoverFg: COLOR['white'],
        border: COLOR['red'],
        hoverBorder: COLOR['red-light'],
      })}
    }
  }

  &.mode-line-dark-yellow {
    border-style: solid;
    border-width: 1px;
    ${ButtonColors({
      bg: COLOR['dark-gray-darkest'],
      fg: COLOR['yellow'],
      hoverBg: COLOR['yellow'],
      hoverFg: COLOR['turquoise-darker'],
      border: COLOR['yellow'],
      hoverBorder: COLOR['yellow'],
    })}

    &.active {
      ${ButtonColors({
        badge: COLOR['yellow-dark'],
        bg: COLOR['yellow'],
        fg: COLOR['white'],
        hoverBg: COLOR['yellow'],
        hoverFg: COLOR['white'],
        border: COLOR['yellow'],
        hoverBorder: COLOR['yellow-light'],
      })}
    }
  }

  &.mode-line-light {
    border-style: solid;
    border-width: 1px;

    ${ButtonColors({
      bg: COLOR['white'],
      fg: COLOR['primary'],
      hoverBg: COLOR['primary'],
      hoverFg: COLOR['white'],
      border: COLOR['primary'],
      hoverBorder: COLOR['primary'],
    })}
  }

  &.mode-light {
    ${ButtonColors({
      bg: COLOR['white'],
      fg: COLOR['turquoise-darker'],
      hoverBg: COLOR['button-dark-hover'],
      hoverFg: COLOR['white'],
    })}
  }

  &.mode-player {
    ${ButtonColors({
      bg: COLOR['dark-gray-light'],
      fg: COLOR['white'],
      hoverBg: COLOR['light-gray'],
      hoverFg: COLOR['text'],
      border: COLOR['white'],
      hoverBorder: COLOR['white'],
    })}

    &:hover {
      path,
      polygon {
        fill: ${COLOR['dark-gray-darkest']};
      }
    }

    fieldset.field-button-group & {
      ${ButtonColors({
        bg: COLOR['dark-gray-light'],
        fg: COLOR['white'],
        hoverBg: COLOR['light-gray'],
        hoverFg: COLOR['text'],
        border: COLOR['dark-gray-light'],
        hoverBorder: COLOR['light-gray'],
      })}
    }

    &.active {
      ${ButtonColors({
        bg: COLOR['white'],
        fg: COLOR['text'],
        hoverBg: COLOR['light-gray'],
        hoverFg: COLOR['text'],
      })}

      path, polygon {
        fill: ${COLOR['dark-gray-darkest']};
      }

      fieldset.field-button-group & {
        ${ButtonColors({
          bg: COLOR['white'],
          fg: COLOR['text'],
          hoverBg: COLOR['white'],
          hoverFg: COLOR['text'],
          border: COLOR['white'],
          hoverBorder: COLOR['white'],
        })}
      }

      &.featured {
        ${ButtonColors({
          bg: COLOR['red'],
          fg: COLOR['white'],
          hoverBg: COLOR['light-gray'],
          hoverFg: COLOR['text'],
        })}

        fieldset.field-button-group & {
          ${ButtonColors({
            bg: COLOR['red'],
            fg: COLOR['white'],
            hoverBg: COLOR['red'],
            hoverFg: COLOR['white'],
            border: COLOR['red'],
            hoverBorder: COLOR['red'],
          })}
        }
      }
    }
  }

  &.mode-light-gray {
    ${ButtonColors({
      bg: COLOR['light-gray'],
      fg: COLOR['text'],
      hoverBg: COLOR['light-gray-light'],
      hoverFg: COLOR['text'],
      border: COLOR['light-gray'],
      hoverBorder: COLOR['light-gray-light'],
    })}

    &:hover {
      path,
      polygon {
        fill: ${COLOR['dark-gray-darkest']};
      }
    }

    fieldset.field-button-group & {
      ${ButtonColors({
        bg: COLOR['light-gray'],
        fg: COLOR['text'],
        hoverBg: COLOR['light-gray-light'],
        hoverFg: COLOR['text'],
        border: COLOR['light-gray'],
        hoverBorder: COLOR['light-gray-light'],
      })}
    }

    // TODO: rest of styles for light-gray button
    &.active {
      ${ButtonColors({
        bg: COLOR['white'],
        fg: COLOR['text'],
        hoverBg: COLOR['light-gray'],
        hoverFg: COLOR['text'],
      })}

      path, polygon {
        fill: ${COLOR['dark-gray-darkest']};
      }

      fieldset.field-button-group & {
        ${ButtonColors({
          bg: COLOR['white'],
          fg: COLOR['text'],
          hoverBg: COLOR['white'],
          hoverFg: COLOR['text'],
          border: COLOR['white'],
          hoverBorder: COLOR['white'],
        })}
      }

      &.featured {
        ${ButtonColors({
          bg: COLOR['red'],
          fg: COLOR['white'],
          hoverBg: COLOR['light-gray'],
          hoverFg: COLOR['text'],
        })}

        fieldset.field-button-group & {
          ${ButtonColors({
            bg: COLOR['red'],
            fg: COLOR['white'],
            hoverBg: COLOR['red'],
            hoverFg: COLOR['white'],
            border: COLOR['red'],
            hoverBorder: COLOR['red'],
          })}
        }
      }
    }
  }

  &.mode-icon-only-no-padding-normal {
    ${ButtonColors({
      bg: COLOR['dark-gray-darkest'],
      fg: COLOR['primary-light'],
      hoverBg: COLOR['primary-light'],
      hoverFg: COLOR['primary-light'],
      border: COLOR['primary-light'],
      hoverBorder: COLOR['primary-light'],
    })}
  }

  &.mode-icon-only-no-padding-red {
    ${ButtonColors({
      bg: COLOR['dark-gray-darkest'],
      fg: COLOR['red-light'],
      hoverBg: COLOR['red-light'],
      hoverFg: COLOR['red-light'],
      border: COLOR['red-light'],
      hoverBorder: COLOR['red-light'],
    })}
  }

  &.mode-orange {
    ${ButtonColors({
      badge: COLOR['orange'],
      bg: COLOR['orange-dark'],
      fg: COLOR['white'],
      hoverBg: COLOR['orange'],
      hoverFg: COLOR['white'],
    })}
  }

  &.mode-red {
    ${ButtonColors({
      badge: COLOR['red-light'],
      bg: COLOR['red'],
      fg: COLOR['white'],
      hoverBg: COLOR['red-dark'],
      hoverFg: COLOR['white'],
    })}
  }
  &.mode-green {
    ${ButtonColors({
      badge: COLOR['green-light'],
      bg: COLOR['green'],
      fg: COLOR['white'],
      hoverBg: COLOR['green-dark'],
      hoverFg: COLOR['white'],
    })}
  }
  &.mode-gray {
    ${ButtonColors({
      badge: COLOR['dark-gray-lighter'],
      bg: COLOR['dark-gray-light'],
      fg: COLOR['white'],
      hoverBg: COLOR['dark-gray'],
      hoverFg: COLOR['white'],
    })}
  }

  &.no-padding {
    background: none !important;
    height: auto !important;
    min-width: 0 !important;
    border-width: 0 !important;
    padding: 0 !important;
    margin: 0 !important;

    .icon {
      margin: 0 !important;
      padding: 0 !important;
    }
  }

  &.primary {
    &.mode-dark {
      ${ButtonColors({
        badge: COLOR['primary-dark'],
        bg: COLOR['primary'],
        fg: COLOR['white'],
        hoverBg: COLOR['primary-dark'],
        hoverFg: COLOR['white'],
      })}
    }

    &.mode-dark-on-dark {
      ${ButtonColors({
        bg: COLOR['turquoise-dark'],
        fg: COLOR['white'],
        hoverBg: COLOR['turquoise'],
        hoverFg: COLOR['turquoise-darker'],
      })}
    }

    &.mode-light {
      ${ButtonColors({
        badge: COLOR['primary-light'],
        bg: COLOR['dark-gray-darkest'],
        fg: COLOR['primary-light'],
        hoverBg: COLOR['primary-dark'],
        hoverFg: COLOR['turquoise-light'],
        border: COLOR['primary-light'],
        hoverBorder: COLOR['button-dark-hover'],
      })}

      &.wire {
        border: 1px solid;
      }

      &.active {
        border: none;

        ${ButtonColors({
          badge: COLOR['primary-dark'],
          bg: COLOR['primary-light'],
          fg: COLOR['white'],
          hoverBg: COLOR['primary-dark'],
          hoverFg: COLOR['white'],
          border: COLOR['turquoise-light'],
          hoverBorder: COLOR['button-dark-hover'],
        })}
      }
    }
  }

  fieldset.field-button-group &,
  &.minor {
    &.mode-dark {
      ${ButtonColors({
        bg: COLOR['white'],
        fg: COLOR['primary'],
        hoverBg: COLOR['primary-dark'],
        hoverFg: COLOR['white'],
        border: COLOR['primary'],
        hoverBorder: COLOR['primary-dark'],
      })}
    }

    &.mode-light {
      ${ButtonColors({
        bg: COLOR['white'],
        fg: COLOR['turquoise-darker'],
        hoverBg: COLOR['button-dark-hover'],
        hoverFg: COLOR['white'],
        border: COLOR['white'],
        hoverBorder: COLOR['button-dark-hover'],
      })}
    }
  }

  fieldset.field-button-group &.active {
    &.mode-dark {
      ${ButtonColors({
        badge: COLOR['primary-dark'],
        bg: COLOR['primary'],
        fg: COLOR['white'],
        hoverBg: COLOR['primary'],
        hoverFg: COLOR['white'],
      })}
    }

    &.mode-light {
      ${ButtonColors({
        badge: COLOR['turquoise-lighter'],
        bg: COLOR['turquoise-light'],
        fg: COLOR['turquoise-darker'],
        hoverBg: COLOR['turquoise-light'],
        hoverFg: COLOR['turquoise-darker'],
        border: COLOR['turquoise-light'],
        hoverBorder: COLOR['turquoise-light'],
      })}
    }
  }

  &.clear {
    border-radius: 0;
    font-size: ${FONT_SIZE['12']};
    height: fit-content;
    margin: ${SIZE['4']} 0;
    min-width: 0;
    padding: 0 ${SIZE['4']};

    &.mode-dark {
      ${ButtonColors({
        badge: COLOR['white'],
        bg: COLOR['transparent'],
        fg: COLOR['primary'],
        hoverBg: COLOR['transparent'],
        hoverFg: COLOR['turquoise-darker'],
      })}
    }

    &.mode-light {
      ${ButtonColors({
        badge: COLOR['turquoise-darker'],
        bg: COLOR['transparent'],
        fg: COLOR['primary'],
        hoverBg: COLOR['transparent'],
        hoverFg: COLOR['turquoise-light'],
      })}
    }

    &.mode-player {
      ${ButtonColors({
        badge: COLOR['light-gray-lighter'],
        bg: COLOR['transparent'],
        fg: COLOR['light-gray-lighter'],
        hoverBg: COLOR['transparent'],
        hoverFg: COLOR['white'],
      })}

      line-height: 21px;
      margin: 0;

      &.icon {
        font-size: ${FONT_SIZE['24']};
      }
    }
  }

  &.pill {
    border-radius: ${buttonHeight};
    font-size: ${FONT_SIZE['12']};

    &.thin {
      border-radius: ${thinButtonHeight};
    }

    &.short {
      border-radius: ${shortButtonHeight};
    }
  }

  &.short {
    line-height: ${shortButtonHeight} - 3px;

    &,
    fieldset.field-button-group & {
      height: ${shortButtonHeight};
    }

    fieldset.field-button-group & {
      line-height: ${shortButtonHeight} - 5px;
    }
  }

  &.control,
  &.thin {
    line-height: ${thinButtonHeight} - 11px;

    &,
    fieldset.field-button-group & {
      height: ${thinButtonHeight};
    }

    fieldset.field-button-group & {
      line-height: ${thinButtonHeight} - 5px;
    }

    .icon {
      font-size: ${FONT_SIZE['16']};
    }

    svg.icon {
      height: ${SIZE['16']};
    }
  }

  &.small {
    font-size: ${FONT_SIZE['11']};
    line-height: ${thinButtonHeight} - 15px;
    height: ${smallButtonHeight} !important;
    min-width: 70px;
    padding: 2px ${SIZE['10']};

    &,
    fieldset.field-button-group & {
      height: ${thinButtonHeight};
    }

    fieldset.field-button-group & {
      line-height: ${thinButtonHeight} - 5px;
    }

    .icon {
      font-size: ${FONT_SIZE['16']};
    }

    svg.icon {
      height: ${SIZE['16']};
    }
  }

  &.control {
    min-width: auto;
    padding: 2px 6px;
  }

  &.full {
    width: 100%;
  }

  &[disabled] {
    cursor: not-allowed;
    pointer-events: none;

    &.mode-dark-on-dark {
      ${ButtonColors({
        badge: COLOR['dark-gray-lighter'],
        bg: COLOR['dark-gray-darker'],
        fg: COLOR['light-gray-darkest'],
        hoverBg: COLOR['dark-gray-darker'],
        hoverFg: COLOR['light-gray-darkest'],
      })}
    }

    &.mode-line-dark {
      ${ButtonColors({
        bg: COLOR['dark-gray-darkest'],
        fg: COLOR['light-gray-darkest'],
        hoverBg: COLOR['dark-gray-darkest'],
        hoverFg: COLOR['light-gray-darkest'],
        border: COLOR['light-gray-darkest'],
        hoverBorder: COLOR['light-gray-darkest'],
      })}
    }

    &.mode-line-light {
      ${ButtonColors({
        bg: COLOR['white'],
        fg: COLOR['dark-gray-lighter'],
        hoverBg: COLOR['white'],
        hoverFg: COLOR['dark-gray-lighter'],
        border: COLOR['dark-gray-lighter'],
        hoverBorder: COLOR['dark-gray-lighter'],
      })}
    }

    &.mode-dark,
    &.mode-light {
      ${ButtonColors({
        badge: COLOR['white'],
        bg: COLOR['light-gray-light'],
        fg: COLOR['dark-gray-lighter'],
        hoverBg: COLOR['light-gray-light'],
        hoverFg: COLOR['dark-gray-lighter'],
      })}
    }

    &.mode-player {
      fieldset.field-button-group &.active {
        ${ButtonColors({
          bg: COLOR['white'],
          fg: COLOR['text'],
          hoverBg: COLOR['white'],
          hoverFg: COLOR['text'],
        })}
      }
    }

    &.mode-light-gray {
      ${ButtonColors({
        bg: COLOR['light-gray-dark'],
        fg: COLOR['light-gray-lighter'],
        hoverBg: COLOR['light-gray-dark'],
        hoverFg: COLOR['light-gray-lighter'],
      })}
    }

    &.clear {
      &.mode-dark,
      &.mode-player {
        ${ButtonColors({
          badge: COLOR['white'],
          bg: COLOR['transparent'],
          fg: COLOR['dark-gray-lighter'],
          hoverBg: COLOR['transparent'],
          hoverFg: COLOR['dark-gray-lighter'],
        })}
      }
      &.mode-light {
        ${ButtonColors({
          badge: COLOR['dark-gray-dark'],
          bg: COLOR['transparent'],
          fg: COLOR['light-gray-light'],
          hoverBg: COLOR['transparent'],
          hoverFg: COLOR['light-gray-light'],
        })}
      }

      fieldset.field-button-group &,
      fieldset.field-button-group &.active {
        cursor: not-allowed;

        &.mode-dark {
          ${ButtonColors({
            badge: COLOR['dark-gray-lighter'],
            bg: COLOR['light-gray-darkest'],
            fg: COLOR['white'],
            hoverBg: COLOR['light-gray-darkest'],
            hoverFg: COLOR['white'],
            border: COLOR['light-gray-darkest'],
            hoverBorder: COLOR['light-gray-darkest'],
          })}
        }
        &.mode-light {
          ${ButtonColors({
            badge: COLOR['dark-gray-lighter'],
            bg: COLOR['light-gray-light'],
            fg: COLOR['dark-gray-lighter'],
            hoverBg: COLOR['light-gray-light'],
            hoverFg: COLOR['dark-gray-lighter'],
            border: COLOR['light-gray-light'],
            hoverBorder: COLOR['light-gray-light'],
          })}
        }
      }

      &.mode-icon-only-no-padding-red {
        opacity: 0.3;
      }
    }
  }

  &.typo-regular {
    font-size: ${FONT_SIZE['14']};
    text-transform: none;
  }

  &.icon-only {
    svg.icon {
      margin-right: 0;
    }
  }

  .icon {
    line-height: ${FONT_SIZE['14']};
    min-width: ${SIZE['12']};
    text-align: center;

    &.icon-front {
      margin-right: 5px;
    }

    &.icon-back {
      margin-left: 5px;
    }

    &.icon-only {
      display: block;
      margin-right: 0;
      padding: 0 ${SIZE['4']};
    }
  }

  .lead-icon {
    margin-right: 10px;
  }

  svg.icon {
    height: ${SIZE['20']};
    margin-right: ${SIZE['4']};
    position: relative;
    width: ${SIZE['20']};
  }

  .text-wrapper {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .badge {
    border-radius: ${badgeSize * 2 + 'px'};
    display: inline-block;
    font-size: ${FONT_SIZE['12']};
    line-height: ${FONT_SIZE['14']};
    margin-right: ${SIZE['8']};
    min-height: ${badgeSize + 'px'};
    min-width: ${badgeSize + 'px'};
    padding: 1px 7px 0px;
    position: relative;
    vertical-align: baseline;
    // ICON ADJUSTMENTS
    .icon {
      position: relative;
    }
  }
`;

// Display Name
Button.displayName = 'Button';

export default Button;
